<template>
  <div
    v-if="appStore.fullscreenLoading.show"
    class="fullscreen-background"
    :class="{
      show: appStore.fullscreenLoading.show,
      close: !appStore.fullscreenLoading.show && wasOpenedOnce,
    }"
  ></div>
  <div
    v-if="appStore.fullscreenLoading.show"
    :style="{
      backgroundColor: appStore.fullscreenLoading.color
        ? `var(--color-${appStore.fullscreenLoading.color}`
        : 'var(--color-brand-01)',
    }"
    :class="[
      'fullscreen-loader',
      appStore.fullscreenLoading.theme
        ? `theme-${appStore.fullscreenLoading.theme}`
        : '',
      {
        show: appStore.fullscreenLoading.show,
        close: !appStore.fullscreenLoading.show && wasOpenedOnce,
      },
    ]"
  >
    <span
      v-if="appStore.fullscreenLoading.show"
      class="name-show-animation name"
      >Hey {{ authStore.user.firstName }},</span
    >
    <span v-if="showText" ref="textElement" class="text-show-animation text">{{
      currentText
    }}</span>

    <BounceLoader v-if="showBounceLoader" kind="indeterminate" class="loader" />

    <a-button
      v-if="showBounceLoader && showCloseButton"
      shape="round"
      class="button-go-to-home"
      @click="goToHome"
    >
      Voltar para a home
    </a-button>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import BounceLoader from '@/components/Loader/Bounce.vue';
import notify from '@/helpers/notify';
import { tracking } from '@/helpers/track-event';
import logger from '@/infra/logger';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { EventName } from '@/types/events';

const appStore = useAppStore();
const authStore = useAuthStore();

const router = useRouter();

const currentText = ref(appStore.fullscreenLoading.texts[0]);
const textElement = ref<HTMLElement | null>(null);
const currentTextIndex = ref(0);
const showText = ref(false);
const showBounceLoader = ref(false);
const textInterval = ref<number>();
const wasOpenedOnce = ref(false);
const openedAt = ref<Date | null>();
const openedAtInterval = ref<number>();
const showCloseButton = ref(false);

function startLoader() {
  currentText.value = appStore.fullscreenLoading.texts[0];
  wasOpenedOnce.value = true;

  setTimeout(() => {
    showCloseButton.value = true;
  }, 20_000);

  setTimeout(() => {
    textInterval.value = setInterval(changeText, 2500);
    showText.value = true;

    setTimeout(() => {
      showBounceLoader.value = true;
    }, 1000);
  }, 2000);
}

function stopLoader() {
  if (textInterval.value) {
    clearInterval(textInterval.value);
  }

  showText.value = false;
  showBounceLoader.value = false;
  showCloseButton.value = false;
}

function goToHome() {
  tracking.trackEvent(EventName.FullscreenLoaderBackToHomeButton);
  router.push('/home');
  setTimeout(
    () => appStore.setFullscreenLoading({ show: false, texts: [] }),
    200,
  );
}

watch(
  () => appStore.fullscreenLoading.show,
  (value) => {
    if (value) {
      startLoader();
      openedAt.value = new Date();

      openedAtInterval.value = setInterval(() => {
        const maxOpenedTime = 100_000;

        if (
          openedAt.value &&
          Date.now() - openedAt.value.getTime() > maxOpenedTime
        ) {
          const error = new Error('Fullscreen loader took too long to load');
          logger.captureException(error);
          appStore.setFullscreenLoading({
            show: false,
            texts: [],
          });
          router.push('/home');
          notify({
            type: 'error',
            message: 'Não foi possível concluír a operação. Tente novamente.',
          });
        }
      }, 1000);
    } else {
      stopLoader();
      openedAt.value = null;

      if (openedAtInterval.value) {
        clearInterval(openedAtInterval.value);
      }
    }
  },
  { immediate: true },
);

function changeText() {
  currentTextIndex.value++;

  if (currentTextIndex.value >= appStore.fullscreenLoading.texts.length) {
    currentTextIndex.value = 0;
  }

  currentText.value = appStore.fullscreenLoading.texts[currentTextIndex.value];
}

// Watch the currentText for changes and retrigger the animation
watch(currentText, () => {
  if (textElement.value) {
    retriggerAnimation(textElement.value);
  }
});

function retriggerAnimation(element: HTMLElement) {
  element.classList.remove('text-show-animation');
  void element.offsetWidth; // Trigger reflow
  element.classList.add('text-show-animation');
}
</script>

<style scoped>
.fullscreen-background {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1000000;
}

.fullscreen-background.show {
  animation: background-animation-show 0.5s forwards;
}

@keyframes background-animation-show {
  from {
    background-color: transparent;
  }
  to {
    background-color: rgba(0, 0, 0, 0.7);
  }
}

.fullscreen-background.close {
  animation: background-animation-close 0.5s forwards;
}

@keyframes background-animation-close {
  from {
    background-color: rgba(0, 0, 0, 0.7);
  }
  to {
    background-color: transparent;
  }
}

.button-go-to-home {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  background-color: transparent;
  color: var(--color-neutral-06);
  border: 1px solid var(--color-neutral-06);
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  width: 156px;
  margin-top: 56px;
}

.fullscreen-loader {
  position: fixed;
  width: 0%;
  height: 0%;
  position: fixed;
  top: 50%;
  left: 50%;
  background-color: var(--color-brand-01);
  display: flex;
  align-items: center;
  z-index: 1000001;
  transform: translate(-50%, -50%);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  padding-left: 10vh;
  padding-right: 10vh;
  opacity: 1;
}

.fullscreen-loader.close {
  animation: close 0.4s forwards;
}

.fullscreen-loader.show {
  animation: grow-stage-1 0.5s forwards, grow-stage-2 0.8s forwards 1.6s;
}

@keyframes close {
  from {
    width: 100%;
    height: 100%;
  }
  to {
    width: 0%;
    height: 0%;
    padding-left: 0px;
    padding-right: 0px;
  }
}

@keyframes grow-stage-1 {
  from {
    width: 0%;
    height: 0%;
  }
  to {
    width: 90%;
    height: 90%;
  }
}

@keyframes grow-stage-2 {
  from {
    width: 90%;
    height: 90%;
  }
  to {
    width: 100%;
    height: 100%;
    border-radius: 0px;
  }
}

.name-show-animation {
  opacity: 0;
  animation: slide-up 0.5s forwards 0.7s;
}

.text-show-animation {
  opacity: 0;
  animation: slide-up 0.8s forwards;
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.name {
  color: var(--color-brand-04);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  margin-top: 25vh;
}

.theme-light .text {
  color: var(--color-contrast-oninteractive);
}
.theme-light .button-go-to-home {
  color: var(--color-contrast-oninteractive);
  border-color: var(--color-contrast-oninteractive);
}

.text {
  color: var(--color-brand-05);
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 112%;
  margin-top: 20px;
  text-align: center;
  min-height: 60px;
}

.loader {
  margin-top: 60px;
}
</style>
